import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import {
  createFileRoute,
  Link,
  linkOptions,
  Outlet,
  useLocation,
} from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import {
  CircleDot,
  Coins,
  CreditCard,
  FileText,
  LockIcon,
  PlusCircle,
  RotateCcw,
  Trash2,
  Type,
  User,
} from "lucide-react";
import { Suspense, useState } from "react";
import { capitalize } from "shared";
import { createColumnConfigHelper } from "@/components/data-table-filter/core/filters";
import type { FiltersState } from "@/components/data-table-filter/core/types";
import { IconButton } from "@/components/icon-button";
import { Page } from "@/components/page";
import { SheetSkeleton } from "@/components/skeletons/sheet-skeleton";
import { RenderStatusBadge } from "@/components/table/cells/RenderStatusBadge";
import { RenderCell } from "@/components/table/cells/render-cell";
import { DataTable } from "@/components/table/DataTable";
import { TablePagination } from "@/components/table/DataTablePagination";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { usePersistedReactTable } from "@/hooks/use-persisted-table";
import { useRequiredRole } from "@/hooks/useRequiredRole";
import type { RouterOutputs } from "@/lib/trpcClient";

type Card = RouterOutputs["card"]["list"][number];
const dtf = createColumnConfigHelper<Card>();

const columnsConfig = [
  dtf
    .text()
    .id("Name")
    .accessor((row) => row.name)
    .displayName("Name")
    .icon(Type)
    .build(),

  dtf
    .number()
    .id("Balance")
    .accessor((row) => row.restriction.periodBalanceCents / 100)
    .displayName("Balance")
    .icon(Coins)
    .build(),

  dtf
    .option()
    .id("Cardholder")
    .accessor((row) => row.cardHolder)
    .displayName("CardHolder")
    .icon(User)
    .transformOptionFn((u) => ({
      label: u.name,
      value: u.id,
    }))
    .build(),

  dtf
    .option()
    .id("Status")
    .accessor((row) => row.status)
    .displayName("Status")
    .icon(CircleDot)
    .transformOptionFn((u) => ({
      label: capitalize(u),
      value: u,
    }))
    .hide()
    .build(),

  dtf
    .option()
    .id("Refresh Interval")
    .accessor((row) => row.restriction.refreshInterval)
    .displayName("Refresh Interval")
    .icon(RotateCcw)
    .transformOptionFn((u) => ({
      label: capitalize(u),
      value: u,
    }))
    .build(),
];

export const Route = createFileRoute("/_authed/_org/cards")({
  component: RouteComponent,
  loader: ({ context: { queryClient, trpc } }) => {
    queryClient.ensureQueryData(trpc.card.list.queryOptions());
  },
});

const initialFilterState: FiltersState = [
  {
    columnId: "Status",
    hide: true,
    operator: "is",
    type: "option",
    values: ["active"],
  },
];

type View = "active" | "frozen" | "canceled";

function RouteComponent() {
  const navigate = Route.useNavigate();
  const { admin, bookkeeper } = useRequiredRole();
  const { trpc } = Route.useRouteContext();
  const location = useLocation();

  const { data, isLoading: isCardsLoading } = useQuery(
    trpc.card.list.queryOptions(),
  );
  const { data: orgMembers, isLoading: isUsersLoading } = useQuery(
    trpc.org.users.queryOptions(),
  );
  const { data: session } = useSuspenseQuery(trpc.auth.session.queryOptions());

  const isLoading = isCardsLoading || isUsersLoading;

  const tableColumns: ColumnDef<Card>[] = [
    {
      accessorFn: (row) => row.name,
      cell: ({ row }) => {
        return <RenderCell card={row.original} type="card" />;
      },
      id: "Name",
    },
    {
      accessorFn: (row) => row.restriction.periodBalanceCents / 100,
      cell: ({ row }) => (
        <RenderCell restriction={row.original.restriction} type="restriction" />
      ),
      id: "Balance",
      minSize: 300,
    },
    {
      accessorFn: (row) => row.type,
      id: "Type",
    },
    {
      accessorFn: (row) => row.restriction.refreshInterval,
      id: "Refresh Interval",
    },
    {
      accessorFn: (row) => row.cardHolderId,
      cell: ({ row }) => {
        return (
          orgMembers && (
            <RenderCell
              type="cardHolder"
              user={orgMembers.userMap[row.original.cardHolderId]}
            />
          )
        );
      },
      id: "Cardholder",
    },
    {
      accessorFn: (row) => row.restriction.allowedCategories,
      cell: ({ row }) => {
        return (
          <RenderCell
            type="categories"
            categories={row.original.restriction.allowedCategories}
          />
        );
      },
      id: "Categories",
    },
    {
      accessorFn: (row) => row.status,
      cell: ({ row }) => <RenderStatusBadge status={row.original.status} />,
      id: "Status",
    },
    {
      accessorFn: (row) => row.createdAt,
      cell: ({ row }) => (
        <RenderCell date={row.original.createdAt ?? new Date()} type="date" />
      ),
      id: "Issued",
    },
  ];

  const tableName = "cards";
  const { table, filters, actions, columns, strategy, setTableFilters } =
    usePersistedReactTable({
      columns: tableColumns,
      data: data ?? [],
      filterOptions: {
        columnsConfig,
        defaultFilters: initialFilterState,
        strategy: "client",
      },
      getRowId: (row) => row.id.toString(),
      initialState: {
        columnVisibility: {
          "Refresh Interval": false,
          Type: false,
        },
      },
      tableName,
    });

  const [view, setView] = useState<View>(getInitialViewFromFilters(filters));

  const updateStatusFilter = (status: View) => {
    setView(status);
    setTableFilters((filters) =>
      filters.map((f) =>
        f.columnId === "Status"
          ? {
              ...f,
              values: [status],
            }
          : f,
      ),
    );
  };

  const issuedCardsCount = data?.filter(
    (card) => card.status === "active",
  ).length;
  const lockedCardsCount = data?.filter(
    (card) => card.status === "frozen",
  ).length;
  const canceledCardsCount = data?.filter(
    (card) => card.status === "canceled",
  ).length;

  const hasSelfCardholderFilter = filters
    .find((i) => i.columnId === "Cardholder")
    ?.values.includes(session.user?.id);

  const myCardsFilterToggle = (
    <IconButton
      icon="user-pen"
      tooltip="My Cards"
      className={
        hasSelfCardholderFilter ? "border border-border bg-neutral-100!" : ""
      }
      onClick={() => {
        if (!hasSelfCardholderFilter)
          setTableFilters((filters) => [
            ...filters.filter((f) => f.columnId !== "Cardholder"),
            {
              columnId: "Cardholder",
              operator: "is",
              type: "option",
              values: [session.user?.id || ""],
            },
          ]);
        else
          setTableFilters((filters) =>
            filters.filter((f) => f.columnId !== "Cardholder"),
          );
      }}
    />
  );

  return (
    <Sheet
      onOpenChange={(open) => {
        if (!open) {
          navigate({
            replace: true,
            to: "/cards",
          });
        }
      }}
      open={
        location.pathname !== "/cards" &&
        location.pathname !== "/cards/" &&
        location.pathname.includes("/cards")
      }
    >
      <Page
        heading="Cards"
        actions={
          <>
            {bookkeeper && (
              <Link
                to="/organization/statements"
                search={{
                  tab: "statements",
                }}
              >
                <Button className="ml-2" variant={"outline"}>
                  <FileText />
                  Statements
                </Button>
              </Link>
            )}

            {admin ? (
              <Link to="/cards/create">
                <Button className="ml-2">
                  <PlusCircle />
                  <span className="font-semibold">Issue Card</span>
                </Button>
              </Link>
            ) : (
              <Link to="/requests/create/card">
                <Button size="sm" variant="default">
                  <CreditCard />
                  Request a new card
                </Button>
              </Link>
            )}
          </>
        }
        table={{
          actions,
          columns,
          filters,
          strategy,
          table,
        }}
        tabs={
          <div className="flex items-center gap-4">
            <Tabs
              onValueChange={(v) => {
                updateStatusFilter(v as View);
              }}
              value={view}
              className="@xl/page:w-auto w-full"
            >
              <TabsList
                variant="standard"
                className="h-fit @xl/page:w-auto w-full @lg/page:flex-row flex-col [&>button]:w-full [&>button]:py-2"
              >
                <TabsTrigger
                  value="active"
                  count={issuedCardsCount}
                  variant="standard"
                >
                  <span className="flex items-center gap-2">
                    <CreditCard className="size-3.5 text-neutral-400" />
                    Issued
                  </span>
                </TabsTrigger>
                <TabsTrigger
                  value="frozen"
                  count={lockedCardsCount}
                  variant="standard"
                >
                  <span className="flex items-center gap-2">
                    <LockIcon className="size-3.5 text-neutral-400" />
                    Locked
                  </span>
                </TabsTrigger>
                <TabsTrigger
                  value="canceled"
                  count={canceledCardsCount}
                  variant="standard"
                >
                  <span className="flex items-center gap-2">
                    <Trash2 className="size-3.5 text-neutral-400" />
                    <span>Canceled</span>
                  </span>
                </TabsTrigger>
              </TabsList>
            </Tabs>

            <div className="@xl/page:block hidden">{myCardsFilterToggle}</div>
          </div>
        }
        secondaryActions={
          <div className="block @xl/page:hidden">{myCardsFilterToggle}</div>
        }
      >
        <DataTable
          noResultsComponent={
            <div className="flex h-36 w-full flex-col items-center justify-center space-y-4">
              <span className="text-xl">No Matching Cards</span>

              <Button onClick={actions?.removeAllFilters} variant={"outline"}>
                Clear Filters
              </Button>
            </div>
          }
          table={table}
          tableName={tableName}
          navOptions={(row) =>
            linkOptions({
              params: {
                id: row.original.id,
              },
              to: "/cards/$id",
            })
          }
          loading={isLoading}
          className="[&_th]:border-t-0!"
        />

        <TablePagination tableName={tableName} table={table} />
      </Page>

      <SheetContent
        className="w-full"
        onEscapeKeyDown={(e) => {
          e.preventDefault();
        }}
      >
        <Suspense
          fallback={
            <div className="px-6">
              <SheetSkeleton />
            </div>
          }
        >
          <Outlet />
        </Suspense>
      </SheetContent>
    </Sheet>
  );
}
function getInitialViewFromFilters(filters: FiltersState): View {
  const cardStatus = filters.find((filter) => filter.columnId === "Status");
  if (!cardStatus) {
    return "active";
  }

  return cardStatus.values[0] as View;
}
